import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar.jsx';
import { MobileTopBar } from './MobileTopBar.jsx';
import { OfflineBanner } from './OfflineBanner.jsx';
import { AppErrorBoundary } from './ErrorBoundary.jsx';
import { coursesRequested } from '../features/courses/store/coursesListSlice.js';
import { selectCoursesStatus } from '../features/courses/store/selectors.js';
import { featureVisibilityRequested } from '../features/appVisibility/store/featureVisibilitySlice.js';
import { selectFeatureVisibilityLoaded, selectFeatureVisibilityReady, selectFeatureVisibilityStatus } from '../features/appVisibility/store/selectors.js';
import { isNativePlatform } from '../services/native/platformService.js';
import { NativeAppShell } from './NativeAppShell.jsx';
import { NativeStartupReadyContext } from './NativeSplashReady.jsx';
import { NativeSessionLoading } from './NativeSessionLoading.jsx';

export function Layout() {
  const dispatch = useDispatch();
  const coursesStatus = useSelector(selectCoursesStatus);
  const featureVisibilityStatus = useSelector(selectFeatureVisibilityStatus);
  const featureVisibilityReady = useSelector(selectFeatureVisibilityReady);
  const featureVisibilityLoaded = useSelector(selectFeatureVisibilityLoaded);
  const native = isNativePlatform();
  const location = useLocation();
  const isOnboarding = location.pathname === '/onboarding';

  // Load the courses list once inside the authenticated shell. Both the
  // coaching gate and the My Assessments tab in the Sidebar read this slice
  // directly; without this, they stay stale until /courses is visited
  // (coursesList is not persisted, so it starts idle on every load).
  // Keep this as the single bootstrap owner so /courses and /assessments do
  // not dispatch a second list request from their page-level effects.
  useEffect(() => {
    if (coursesStatus === 'idle') dispatch(coursesRequested());
  }, [dispatch, coursesStatus]);

  useEffect(() => {
    if (featureVisibilityStatus === 'idle') dispatch(featureVisibilityRequested());
  }, [dispatch, featureVisibilityStatus]);

  if (native) {
    // The launch splash has already been dismissed on sign-in. Gate the
    // authenticated shell itself so no empty/default tabs appear before MyStep.
    if (!isOnboarding && !featureVisibilityLoaded) {
      return (
        <NativeSessionLoading
          failed={featureVisibilityStatus === 'failed'}
          onRetry={() => {
            dispatch(featureVisibilityRequested());
            if (coursesStatus === 'failed') dispatch(coursesRequested());
          }}
        />
      );
    }
    // Keep bootstrap requests running under the splash. Failed requests also
    // settle startup so the existing error/retry UI remains reachable offline.
    const startupReady = isOnboarding || (
      featureVisibilityReady && ['succeeded', 'failed'].includes(coursesStatus)
    );
    return (
      <NativeStartupReadyContext.Provider value={startupReady}>
        <NativeAppShell />
      </NativeStartupReadyContext.Provider>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-paper text-ink">
      <OfflineBanner />
      {/* On large screens use a 240px sidebar grid (mirrors surfaces.css
          .app-shell shape) but rely on Tailwind utilities so we keep our
          existing scroll model. .app-shell itself uses overflow:hidden
          which fights long pages, so we don't use the literal class.
          On small screens we fall back to a single column and the
          MobileTopBar surfaces the hamburger that opens the same Sidebar
          in a Radix Dialog. */}
      <div className="flex-1 min-h-0 lg:grid lg:grid-cols-[240px_1fr] flex flex-col">
        {/* Sticky sidebar wrapper — keeps the sidebar visible while the
            main pane scrolls. lg:h-screen anchors it; lg:sticky lg:top-0
            pins it. Without these, long pages (course detail, lesson
            player) would scroll the sidebar out of view. */}
        <div className="hidden lg:block lg:sticky lg:top-0 lg:h-screen">
          <Sidebar waitForFeatureVisibility />
        </div>
        {/* Right column. We deliberately do NOT apply surfaces.css's
            .main-pane class here: it sets overflow-y:auto on the inner
            <main>, which converts page-body scroll to element-inner
            scroll and silently breaks ScrollToTop's window.scrollTo. It
            also has unlayered CSS padding that wins against Tailwind's
            layered @utility padding. min-w-0 stays to prevent grid-cell
            overflow from long content. */}
        <div className="min-w-0 flex flex-col">
          {!isOnboarding && <MobileTopBar />}
          <main className={`flex-1 w-full max-w-content mx-auto lg:px-10 lg:py-12${isOnboarding ? ' onboarding-main' : ' px-6 py-8'}`}>
            <AppErrorBoundary>
              <Outlet />
            </AppErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  );
}
